import { useEffect, useCallback } from 'react';
import { Outlet, useSearchParams, useNavigate } from 'react-router-dom';
import { REVIEWS } from '../../constants/routes';
import { useSelector, useDispatch } from 'react-redux';
import { checkboxClasses } from '@mui/material/Checkbox';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

import Box from '@mui/material/Box';
import Container from '@mui/material/Container';

import NavBar from '../NavBar/NavBar';
import SideNav from '../SideNav/SideNav';
import { toggleSideNav } from '../SideNav/slice';
import {
	hideIFrameContent,
	getEunaOrangeColor,
	getEcivisBlueColor,
	getOldBlueColor
} from '../../utils/utilFunctions';
import usePermissions from '../../hooks/usePermissions';
import BreadCrumb from '../BreadCrumb/BreadCrumb';

const mdTheme = createTheme({
	palette: {
		primary: {
			main: `${getOldBlueColor()}`
		},
		secondary: {
			main: `${getEunaOrangeColor()}`
		}
	},
	components: {
		MuiTab: {
			styleOverrides: {
				root: {
					'&.Mui-selected': {
						color: `${getEcivisBlueColor()}`
					}
				}
			}
		},
		MuiListItemButton: {
			styleOverrides: {
				root: {
					'&.Mui-selected': {
						backgroundColor: '#F9FAFE'
					}
				}
			}
		},
		MuiInput: {
			defaultProps: {
				disableUnderline: true
			}
		},
		MuiInputLabel: {
			defaultProps: {
				shrink: true,
				disableAnimation: true
			},
			styleOverrides: {
				root: {
					overflow: 'hidden',
					'&.Mui-error.Mui-disabled': {
						color: 'rgba(0,0,0,0.38)'
					},
					'&.Mui-error:not(.Mui-disabled)': {
						color: 'rgba(0, 0, 0, 0.6)'
					}
				}
			}
		},
		MuiInputBase: {
			styleOverrides: {
				root: {
					'&.Mui-disabled p:not(.elementId)': {
						color: 'rgba(0,0,0,0.38)'
					}
				}
			}
		},
		MuiCheckbox: {
			styleOverrides: {
				root: {
					[`&.${checkboxClasses.checked}`]: {
						color: `${getOldBlueColor()}`,
						'&.Mui-disabled': {
							color: 'rgba(0,0,0,0.38)'
						}
					}
				}
			}
		}
	}
});

const BaseLayout = () => {
	const [searchParams] = useSearchParams();
	const sideNavState = useSelector((state) => state.sideNav.value);
	const dispatch = useDispatch();
	const { userAccountTypeID } = usePermissions();
	const navigate = useNavigate();
	const isIframe = searchParams?.get('iFrame') === 'true';

	// Open and Close SideNav
	const toggleDrawer = () => {
		dispatch(toggleSideNav());
	};

	// Handle postMessage from parent window (portal-client)
	const handleParentMessage = useCallback(
		(event) => {
			if (!event.data || !event.data.type) return;

			if (event.data.type === 'NAVIGATE_TO_REVIEWS') {
				navigate(REVIEWS + (isIframe ? '?iFrame=true' : ''));
			}
		},
		[navigate, isIframe]
	);

	// Listen for messages from parent window when in iframe
	useEffect(() => {
		if (!isIframe) return;

		window.addEventListener('message', handleParentMessage);
		return () => {
			window.removeEventListener('message', handleParentMessage);
		};
	}, [isIframe, handleParentMessage]);

	useEffect(() => {
		isIframe && hideIFrameContent();
	}, [isIframe]);

	return (
		<ThemeProvider theme={mdTheme}>
			<Box sx={{ display: 'flex', marginTop: '64px' }} data-testid="baseLayout-mainContent">
				<CssBaseline />

				<NavBar
					open={sideNavState}
					toggleDrawer={toggleDrawer}
					userAccountTypeID={userAccountTypeID}
				/>

				{/* DRAWER/SIDENAV HERE */}
				{userAccountTypeID && searchParams?.get('iFrame') !== 'true' && (
					<SideNav open={sideNavState} toggleDrawer={toggleDrawer} />
				)}

				{/* MAIN CONTENT */}
				<Box
					id={'baselayout-box'}
					data-testid={'baselayout-box'}
					component="main"
					sx={{
						flexGrow: 1,
						height: 'calc(100vh - 74px)',
						overflow: 'auto'
					}}
				>
					<Container
						data-testid={'baselayout-container'}
						sx={{
							maxWidth: '100%!important',
							paddingLeft: '37px!important',
							mt: 4,
							mb: 4
						}}
					>
						<BreadCrumb />
						<Outlet />
					</Container>
				</Box>
			</Box>
		</ThemeProvider>
	);
};

export default BaseLayout;
